import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Frame, Box, Layers } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const ITEMS = [
  {
    name: "Portrait Illusions",
    icon: Frame,
    type: "qty",
    base: 169,
    q2add: 140,
    q3add: 180,
    unit: "portrait",
    desc: "Mind-bending portrait illusions that reveal hidden imagery and surprise the eye.",
  },
  {
    name: "Custom 3D Design",
    icon: Box,
    type: "qty",
    base: 699,
    q2add: 560,
    q3add: 480,
    unit: "design",
    desc: "Custom three-dimensional Character designs and renders built to make your concept pop.",
    note: "*Please Note: 3D Designs require 3-6 weeks for completion. Updates are provided after each milestone.",
  },
  {
    name: "Card Game Creator Package",
    icon: Layers,
    type: "cards",
    options: [
      { count: 18, price: 1849 },
      { count: 36, price: 2499 },
    ],
    unit: "card",
    desc: "A complete custom card game package — fully illustrated and print-ready.",
    note: "*Please Note: Card Game Creator Package require 3-6 months for completion. Updates are provided after each milestone.",
  },
];

const QTYS = [1, 2, 3];

const priceForQty = (item, qty) => {
  if (qty === 1) return item.base;
  const q2 = item.base + item.q2add;
  if (qty === 2) return q2;
  return q2 + item.q3add;
};

const ProjectCard = ({ item }) => {
  const Icon = item.icon;
  const slug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const isCards = item.type === "cards";

  const [qty, setQty] = useState(1);
  const [cardCount, setCardCount] = useState(isCards ? item.options[0].count : 0);

  const price = isCards
    ? item.options.find((o) => o.count === cardCount).price
    : priceForQty(item, qty);

  const selectionLabel = isCards ? `${cardCount} cards` : `${qty} ${item.unit}${qty > 1 ? "s" : ""}`;
  const orderState = isCards
    ? `Special Projects — ${item.name} (${cardCount} cards)`
    : `Special Projects — ${item.name} (qty ${qty})`;
  const fromLabel = isCards
    ? `From $${item.options[0].price.toLocaleString()} / ${item.options[0].count} cards`
    : `From $${item.base.toLocaleString()} / 1 ${item.unit}`;

  return (
    <div
      data-testid={`special-project-${slug}`}
      className="brutal-border brutal-shadow bg-white p-7 sm:p-9 flex flex-col"
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-[#EAF1F8] text-[#3E6FA8] flex items-center justify-center">
          <Icon size={26} strokeWidth={2} />
        </div>
        <div>
          <h2 className="font-display text-2xl sm:text-3xl text-[#1F2530] leading-none">
            {item.name}
          </h2>
          <p className="text-sm font-semibold uppercase tracking-wider text-[#3E6FA8] mt-1">
            {fromLabel}
          </p>
        </div>
      </div>

      <p className="mt-5 leading-relaxed text-[#1F2530]/70">{item.desc}</p>
      {item.note && (
        <p
          data-testid={`special-note-${slug}`}
          className="mt-3 text-sm font-semibold italic text-red-600"
        >
          {item.note}
        </p>
      )}

      <div className="mt-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2530]/60 mb-3">
          {isCards ? "Number of cards" : "Quantity"}
        </p>
        <div className="flex flex-wrap gap-2">
          {isCards
            ? item.options.map((o) => (
                <button
                  key={o.count}
                  data-testid={`special-cards-${slug}-${o.count}`}
                  onClick={() => setCardCount(o.count)}
                  className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                    cardCount === o.count
                      ? "bg-[#3E6FA8] text-white border-[#3E6FA8]"
                      : "bg-white text-[#1F2530] border-[#E3E5E9] hover:border-[#3E6FA8] hover:text-[#3E6FA8]"
                  }`}
                >
                  {o.count} cards
                </button>
              ))
            : QTYS.map((q) => (
                <button
                  key={q}
                  data-testid={`special-qty-${slug}-${q}`}
                  onClick={() => setQty(q)}
                  className={`min-w-[56px] px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                    qty === q
                      ? "bg-[#3E6FA8] text-white border-[#3E6FA8]"
                      : "bg-white text-[#1F2530] border-[#E3E5E9] hover:border-[#3E6FA8] hover:text-[#3E6FA8]"
                  }`}
                >
                  {q}
                </button>
              ))}
        </div>
      </div>

      <div className="mt-7 pt-6 border-t border-[#E3E5E9] flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2530]/60">
            {selectionLabel}
          </p>
          <p
            data-testid={`special-price-${slug}`}
            className="inline-block mt-2 text-base font-bold uppercase tracking-wider bg-[#EAF1F8] text-[#3E6FA8] px-3 py-1.5 rounded-full"
          >
            ${price.toLocaleString()}
          </p>
        </div>
        <Link
          to="/contact"
          state={{ service: orderState }}
          data-testid={`special-order-${slug}`}
          className="shrink-0 inline-flex items-center gap-2 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider text-sm px-5 py-3"
        >
          Order <ArrowRight size={18} strokeWidth={2.5} />
        </Link>
      </div>
    </div>
  );
};

export default function SpecialProjects() {
  return (
    <div className="App">
      <Navbar />

      {/* HEADER */}
      <section className="bg-[#1F2530] text-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Link
            to="/"
            data-testid="special-projects-back"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#F3F4F6]/70 hover:text-[#7BA7D9] transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={2.5} /> Back home
          </Link>
          <span className="block mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#7BA7D9]">
            Special Projects
          </span>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mt-3">
            Special Projects
          </h1>
          <p className="mt-5 max-w-2xl text-lg font-medium text-[#F3F4F6]/70">
            One-of-a-kind creative work. Pick a project and your options to see pricing
            instantly. Portrait Illusions start at{" "}
            <span className="font-bold text-[#7BA7D9]">$169</span>, Custom 3D Design at{" "}
            <span className="font-bold text-[#7BA7D9]">$699</span>, and the Card Game
            Creator Package at <span className="font-bold text-[#7BA7D9]">$1,849</span>.
          </p>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="bg-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          {/* 3D DESIGN MILESTONES */}
          <div
            data-testid="custom-3d-milestones"
            className="brutal-border brutal-shadow bg-[#3E6FA8] text-white p-7 sm:p-10"
          >
            <h2 className="font-display text-3xl sm:text-4xl">
              Custom 3D Design Milestones
            </h2>
            <div className="mt-6 grid sm:grid-cols-3 gap-6">
              {[
                {
                  stage: "Stage 1",
                  title: "Concept and Modeling",
                  task: "This phase translates initial ideas or 2D sketches into 3D geometry.",
                  timeline: "1 week",
                },
                {
                  stage: "Stage 2",
                  title: "UV Unwrapping and Texturing",
                  task: 'Once the model is built, it\u2019s "unwrapped" into a 2D plane. This allows the application of materials, colors, and textures accurately so the model looks realistic or stylized.',
                  timeline: "2-3 weeks",
                },
                {
                  stage: "Stage 3",
                  title: "Lighting and Rendering",
                  task: "In the final phase, virtual cameras are set up, lights are placed to create mood and shadows, and a render engine is used to process all the elements into a final 2D image.",
                  timeline: "2 weeks",
                },
              ].map((m) => (
                <div key={m.title} className="border-l-2 border-white/30 pl-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                    {m.stage}
                  </p>
                  <h3 className="font-display text-xl mt-1">{m.title}</h3>
                  <p className="mt-2 text-sm text-white/85">{m.task}</p>
                  <p className="mt-2 text-sm text-white/85">
                    <span className="font-semibold">Timeline:</span> {m.timeline}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CARD GAME MILESTONES */}
          <div
            data-testid="card-game-milestones"
            className="mt-14 brutal-border brutal-shadow bg-[#3E6FA8] text-white p-7 sm:p-10"
          >
            <h2 className="font-display text-3xl sm:text-4xl">
              Card Game Creator Package Milestones
            </h2>
            <div className="mt-6 grid sm:grid-cols-3 gap-6">
              {[
                {
                  stage: "Stage 1",
                  title: "Concept & Core Mechanics",
                  task: "The game\u2019s objective is Define, core gameplay loop, and theme, then create a foundational 1\u20132 page rule document outlining the basic mechanics.",
                  timeline: "2-4 weeks",
                },
                {
                  stage: "Stage 2",
                  title: "Prototyping & Playtesting",
                  task: "A basic prototype is created and used to conduct blind playtests to identify gameplay issues and refine the rules.",
                  timeline: "4-8 weeks",
                },
                {
                  stage: "Stage 3",
                  title: "Balancing & Polish",
                  task: "Refine game mechanics, balance card stats, improve the visual design, and conduct expanded playtesting to ensure smooth, engaging gameplay.",
                  timeline: "8-12 weeks",
                },
              ].map((m) => (
                <div key={m.title} className="border-l-2 border-white/30 pl-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                    {m.stage}
                  </p>
                  <h3 className="font-display text-xl mt-1">{m.title}</h3>
                  <p className="mt-2 text-sm text-white/85">{m.task}</p>
                  <p className="mt-2 text-sm text-white/85">
                    <span className="font-semibold">Timeline:</span> {m.timeline}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-8"
            data-testid="special-projects-grid"
          >
            {ITEMS.map((item) => (
              <ProjectCard key={item.name} item={item} />
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 brutal-border brutal-shadow bg-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-[#1F2530]">
                Have a wild idea?
              </h2>
              <p className="mt-2 font-medium text-[#1F2530]/70">
                Tell us about your special project and we'll bring it to life.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="special-projects-cta"
              className="shrink-0 inline-flex items-center gap-3 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-7 py-4"
            >
              Start Your Order <ArrowRight size={20} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
